import { CoHostScope, type SchedulingEventType } from '../../../models/Scheduling'

type HostStateInput = Pick<SchedulingEventType, 'accountPageVisible' | 'hostSummary'>

export type HostWarning = 'needs-hosts' | 'no-eligible-host' | 'some-ineligible'

export type AccountPageBannerKind = 'empty' | 'none-live' | 'some-not-live'

export interface AccountPageBanner {
  kind: AccountPageBannerKind
  total: number
  notLiveCount: number
}

export const hostWarning = (eventType: HostStateInput): HostWarning | null => {
  const summary = eventType.hostSummary
  if (!summary) {
    return null
  }
  if (summary.hostCount === 0) {
    return 'needs-hosts'
  }
  if (summary.eligibleHostCount === 0) {
    return 'no-eligible-host'
  }
  if (summary.eligibleHostCount < summary.hostCount) {
    return 'some-ineligible'
  }
  return null
}

export const isOnAccountPage = (eventType: HostStateInput, accountBookingEnabled: boolean): boolean =>
  accountBookingEnabled && Boolean(eventType.accountPageVisible)

export const accountPageLiveCount = (eventTypes: HostStateInput[], accountBookingEnabled: boolean): number | null => {
  const onPage = eventTypes.filter((eventType) => isOnAccountPage(eventType, accountBookingEnabled))
  if (onPage.some((eventType) => eventType.hostSummary == null)) {
    return null
  }
  return onPage.filter((eventType) => (eventType.hostSummary?.eligibleHostCount ?? 0) > 0).length
}

const isKnownNotLive = (eventType: HostStateInput): boolean => eventType.hostSummary != null && eventType.hostSummary.eligibleHostCount === 0

export const accountPageBanner = (eventTypes: HostStateInput[], accountBookingEnabled: boolean): AccountPageBanner | null => {
  if (!accountBookingEnabled) {
    return null
  }
  const onPage = eventTypes.filter((eventType) => Boolean(eventType.accountPageVisible))
  if (onPage.length === 0) {
    return { kind: 'empty', total: 0, notLiveCount: 0 }
  }
  const notLiveCount = onPage.filter(isKnownNotLive).length
  if (notLiveCount === 0) {
    return null
  }
  return { kind: notLiveCount === onPage.length ? 'none-live' : 'some-not-live', total: onPage.length, notLiveCount }
}

export const isCoHostOf = (eventType: Pick<SchedulingEventType, 'myCoHostScopes'>): boolean => (eventType.myCoHostScopes ?? []).length > 0

export const isIndividualCoHostOf = (eventType: Pick<SchedulingEventType, 'myCoHostScopes'>): boolean =>
  (eventType.myCoHostScopes ?? []).includes(CoHostScope.INDIVIDUAL)
